import {take} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {usePrevious} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {CompanyModel} from '../../../../../../libs/shared/core/src/models/api/company/companyModel';
import {ConversationSummaryStatesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {isConversationSummaryFact} from '../../../../../../libs/shared/core/src/models/api/factModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {getHasConversationSynopsis} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../models/api/conversationModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {getIsAiSynopsisEnabled} from '../../../../../store/data/company/companySelectors';
import {computeIsUnableToSummarizeConversation} from '../../../../../store/data/conversations/conversationsHelpers';
import {InboxesVisualConversationSelection} from '../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {useConversationSearch} from '../highlights/conversationSearchContext';
import {ConversationHeaderAiSummarizeButton} from './aiSummarize/conversationHeaderAiSummarizeButton';
import {ConversationHeaderAiSummarizeEmptyState} from './aiSummarize/conversationHeaderAiSummarizeEmptyState';
import {ConversationHeaderAiSummarizeErrorState} from './aiSummarize/conversationHeaderAiSummarizeErrorState';
import {ConversationHeaderAiSummarizeLoadingState} from './aiSummarize/conversationHeaderAiSummarizeLoadingState';
import {ConversationHeaderOverflowButton} from './conversationHeaderOverflowButton';
import {ConversationHeaderSearch} from './conversationHeaderSearch';
import {ConversationSubjectWrapper} from './conversationSubjectWrapper';
import {ConversationSynopsisEntrypoint} from './synopsis/conversationSynopsisEntrypoint';
import {ConversationHeaderTagPillContainer} from './tag/conversationHeaderTagPillContainer';
import {ConversationHeaderTopicContainer} from './topic/conversationHeaderTopicContainer';

/*
 * Constants.
 */

const collapsedHeaderPillsLimit = 10;
const paddingRightWhenExpanded = 48; // px
const maxHeightWhenExpanded = 300; // px

/*
 * Props.
 */

export interface ConversationHeaderProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  teammate: TeammateModel;
  conversationSelection: InboxesVisualConversationSelection;
  conversations: ReadonlyArray<ConversationBaseModel>;
  topics: ReadonlyArray<TopicModel>;
  tags: ReadonlyArray<TagModel>;
  onlyConversation: ConversationModel | undefined;
  isUserVisitor: boolean;
  onSubjectChange: (arg: string) => void;
  inboxesParameters: InboxesParameters;
  company: CompanyModel;
  contentPage: ViewerPanelPageState;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  grid-area: header;
  background-color: ${(p) => p.theme.appBackground};
`;

const StyledContentDiv = styled.div`
  display: flex;
  padding: 6px 8px 6px 16px;
  padding-left: 2px;
  padding-right: 11px;
  min-height: 46px;
`;

const StyledLeftDiv = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  position: relative;
`;

const StyledTagsDiv = styled.div<{$isExpanded: boolean}>`
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  padding: 3px 0;
  padding-left: 8px;
  padding-right: ${(p) => (p.$isExpanded ? paddingRightWhenExpanded : 0)}px;

  max-height: ${maxHeightWhenExpanded}px;
`;

const StyledSummaryDiv = styled.div`
  display: flex;
  padding: 0px 10px 8px 2px;
  margin-top: -4px;
  min-height: 40px;

  &:empty {
    display: none;
  }
`;

/*
 * Component.
 */

export const ConversationHeader: FC<ConversationHeaderProps> = (props) => {
  const [isExpanded, setExpand] = useState(false);
  const {
    cellsContext,
    conversations,
    onlyConversation,
    onSubjectChange,
    panelId,
    isUserVisitor,
    teammate,
    company,
  } = props;
  const previousConversationId = usePrevious(onlyConversation?.id);
  const previousSummaryState = usePrevious(onlyConversation?.summaryState);

  const hasConversationSynopsisFF = useAppSelector(getHasConversationSynopsis);
  const isAiSynopsisEnabled = useAppSelector(getIsAiSynopsisEnabled);
  const shouldDisplaySynopsis = hasConversationSynopsisFF && isAiSynopsisEnabled;

  // We should only be able to search in P3 if a conversation search context is available.
  const conversationSearchContext = useConversationSearch();

  if (conversations.length === 0) {
    return null;
  }

  // If the conversation Ids match, and the conversation was previously summarizing, then the summary was newly generated.
  const isMatchingConvo = previousConversationId === onlyConversation?.id;
  const wasGeneratingSummary = Boolean(
    onlyConversation?.summaryState !== ConversationSummaryStatesEnum.LOADING &&
      previousSummaryState === ConversationSummaryStatesEnum.LOADING,
  );
  const isSummaryNewlyGenerated = isMatchingConvo && wasGeneratingSummary;

  const summary = onlyConversation?.facts.find(isConversationSummaryFact);
  const shouldExpandSummary = isSummaryNewlyGenerated;

  const renderSummarizeSection = () => {
    // When they have conversation synopsis, we do not need to show the summarize button.
    if (shouldDisplaySynopsis) {
      return null;
    }

    if (summary) {
      return (
        <ConversationHeaderAiSummarizeButton
          conversation={onlyConversation}
          summary={summary}
          key={summary.id}
          shouldExpandSummary={shouldExpandSummary}
          teammate={teammate}
        />
      );
    }

    const {isSummarizeDisabled} = computeIsUnableToSummarizeConversation(cellsContext, onlyConversation);

    // If the user is a visitor, the company has AI disabled or is in an invalid location, do not show the generate button.
    if (
      isUserVisitor ||
      !company.preferences.isAiSummarizeEnabled ||
      isSummarizeDisabled ||
      !onlyConversation
    ) {
      return null;
    }

    if (onlyConversation.summaryState === ConversationSummaryStatesEnum.LOADING) {
      return (
        <ConversationHeaderAiSummarizeLoadingState
          key={onlyConversation.id}
          teammate={teammate}
          conversationId={onlyConversation.id}
          summaryState={onlyConversation.summaryState}
        />
      );
    }
    if (
      onlyConversation.summaryState === ConversationSummaryStatesEnum.FAILED_RATE_LIMITED ||
      onlyConversation.summaryState === ConversationSummaryStatesEnum.FAILED_OTHER
    ) {
      return (
        <ConversationHeaderAiSummarizeErrorState
          key={onlyConversation.id}
          teammate={teammate}
          conversationId={onlyConversation.id}
          summaryState={onlyConversation.summaryState}
        />
      );
    }

    if (onlyConversation.summaryState === undefined) {
      return (
        <ConversationHeaderAiSummarizeEmptyState
          key={onlyConversation.id}
          teammate={teammate}
          conversationId={onlyConversation.id}
        />
      );
    }

    return null;
  };

  return (
    <StyledHeaderDiv>
      <StyledContentDiv>
        <StyledLeftDiv>
          {onlyConversation ? (
            <ConversationSubjectWrapper
              cellsContext={cellsContext}
              conversation={onlyConversation}
              panelId={panelId}
              onSubjectChange={onSubjectChange}
              isUserVisitor={isUserVisitor}
            />
          ) : null}
          {maybeRenderTopicsAndTags(props, isExpanded, () => setExpand(!isExpanded))}
          {onlyConversation && conversationSearchContext ? (
            <ConversationHeaderSearch
              key={onlyConversation.id}
              conversationSearchContext={conversationSearchContext}
            />
          ) : null}
        </StyledLeftDiv>
      </StyledContentDiv>
      <StyledSummaryDiv>{renderSummarizeSection()}</StyledSummaryDiv>
      {shouldDisplaySynopsis && onlyConversation && !isUserVisitor && (
        <StyledSummaryDiv>
          <ConversationSynopsisEntrypoint
            panelId={panelId}
            page={props.contentPage}
            conversation={onlyConversation}
          />
        </StyledSummaryDiv>
      )}
    </StyledHeaderDiv>
  );
};

function maybeRenderTopicsAndTags(
  props: ConversationHeaderProps,
  isExpanded: boolean,
  onToggleExpandClick: () => void,
) {
  // We need at least one conversation to proceed.
  const {teammate, conversationSelection, topics, tags, isUserVisitor} = props;
  if (isUserVisitor) {
    return null;
  }

  const totalCount = topics.length + tags.length;
  if (totalCount === 0) {
    return null;
  }

  // Find how many pills we need to display.
  const limit = isExpanded ? Number.POSITIVE_INFINITY : collapsedHeaderPillsLimit;
  const overflowCount = totalCount - collapsedHeaderPillsLimit;

  // Filter the collections.
  const visibleTopics = take(topics, limit);
  const visibleTags = take(tags, limit - visibleTopics.length);
  const hasSinglePrivateTag = tags.filter((t) => t.namespace === teammate.namespace).length === 1;

  // Otherwise, render.
  return (
    <StyledTagsDiv $isExpanded={isExpanded}>
      {visibleTopics.map((t) => (
        <ConversationHeaderTopicContainer
          key={t.id}
          panelId={props.panelId}
          conversation={props.onlyConversation}
          teammateId={teammate.id}
          conversationSelection={conversationSelection}
          topic={t}
        />
      ))}
      {visibleTags.map((t) => (
        <ConversationHeaderTagPillContainer
          key={t.id}
          teammate={teammate}
          conversationSelection={conversationSelection}
          tag={t}
          isLastPrivateTag={t.namespace === teammate.namespace && hasSinglePrivateTag}
          panelId={props.panelId}
          inboxesParameters={props.inboxesParameters}
          cellsContext={props.cellsContext}
        />
      ))}
      {overflowCount > 0 && (
        <ConversationHeaderOverflowButton
          value={overflowCount}
          isExpanded={isExpanded}
          onClick={onToggleExpandClick}
        />
      )}
    </StyledTagsDiv>
  );
}
